import { toFixed, signedValue, isEmpty } from "../../../utils/helpers";
import { findSizeBreakupList } from "../../../helpers/orderHelper";
import { MarkerType, AlertParemeter, OState } from "../../../utils/types";
import { colorSecondary, colorTomatoRed, colorLemom, colorLightGreen, colorOrange, colorPrimary, colorPurple } from "../../../utils/colors";
import { UOM } from "../../../constants";

const UomCodeFromValue = Object.values(UOM).reduce((acc, uom) => ({ ...acc, [uom.value]: uom.code }), {});

export const DataType = {
  CUT_SUMMARY: "CutSummary",
  FABRIC_UTILIZATION_SUMMARY: "FabricUtilizationSUmmary",
  WASTAGE_SUMMMARY: "WastageSummary",
  CONSUMPTION_SUMMARY: "ConsumptionSummary",
  ROLL_WIDTH_DEVIATION: "RollWidthDeviation",
  ROLL_LENGTH_VARIATION: "RollLengthVariation",
  MARKER_EFFICIENCY: "MarkerEfficiency",
  FABRIC_UTILIZATION_DETAILS: "FabricUtilizationDetails",
  REMARKS_SUMMARY: "RemarksSummary"
};

export const getHeader = ({ dataType, order, orderFlow }) => {
  let columns = [];
  if (dataType === DataType.CUT_SUMMARY) {
    const sizeList = findSizeBreakupList(orderFlow, order).map(({ size, sizeKey }) => ({ dataKey: sizeKey, title: size })) || [];
    columns = [
      { dataKey: "label", title: "Quantity" },
      ...sizeList,
      { dataKey: "total", title: "Total" },
      { dataKey: "totalPercent", title: "Total(%)" }
    ];
  } else if (dataType === DataType.FABRIC_UTILIZATION_DETAILS) {
    columns = [
      { dataKey: "serialNo", title: "Sl. No." },
      { dataKey: "classification", title: "Classification" },
      { dataKey: "length", title: "Fabric Qty" },
      { dataKey: "percentage", title: "Percentage" },
      { dataKey: "remarks", title: "Remarks" }
    ];
  } else if (dataType === DataType.REMARKS_SUMMARY) {
    columns = [
      { dataKey: "params", title: "Parameter" },
      { dataKey: "remarks", title: "Reason/Remarks" },
      { dataKey: "user", title: "User" }
    ];
  }
  return columns;
};

export const getData = ({ dataType, order, orderFlow, styleColor }) => {
  let rows = [];
  if (dataType === DataType.CUT_SUMMARY && order && orderFlow && orderFlow.markerList && styleColor) {
    const sizeBreakupList = findSizeBreakupList(orderFlow, order);
    const sizeKeyList = sizeBreakupList.map(({ sizeKey }) => sizeKey);

    // Process Order Qty
    const orderQty = order.fpoSizeBreakupList
      .filter(sb => sb.styleColor === styleColor && sb.productId === orderFlow.product?.id)
      .reduce((acc, sb) => ({ ...acc, [sb.size]: sb.qty }), {});
    let totalOrderQty = Object.keys(orderQty).reduce((acc, key) => acc + orderQty[key], 0);
    rows.push({ ...orderQty, styleColor, label: "Order Qty", total: totalOrderQty, totalPercent: "100.0%" });

    let commCutQty = {};
    for (let lot = 1; lot <= orderFlow.lot; lot++) {
      let flow = order.orderFlowList.find(
        e =>
          e.styles === orderFlow.styles &&
          e.colors === orderFlow.colors &&
          e.product?.id === orderFlow.product?.id &&
          e.part === orderFlow.part &&
          e.lot === lot
      );
      let markers =
        flow?.markerList?.filter(
          m =>
            m.markerType === MarkerType.FINAL ||
            m.markerType === MarkerType.REMNANT ||
            m.markerType === MarkerType.MANUAL ||
            m.markerType === MarkerType.RE_CUTTING
        ) || [];

      const cutQty = sizeKeyList.reduce((acc, sizeKey) => {
        const qty = markers
          .filter(m => m.sizeRatioList.find(sr => sr.sizeKey === sizeKey) !== undefined)
          .reduce((acc, m) => {
            const sizeRatio = m.sizeRatioList.find(sr => sr.sizeKey === sizeKey);
            const markerPly = m.markerPlyList.find(mp => mp.styleColor === styleColor);
            const plies = markerPly ? markerPly.actualPlies : 0;
            return acc + sizeRatio.ratio * plies;
          }, 0);
        return {
          ...acc,
          [sizeKey]: qty
        };
      }, {});

      const reCutGarmentQtyMap = JSON.parse(flow?.orderConfig.reCutGarmentQty || "{}");
      const rejectedQtyMap = JSON.parse(flow?.orderConfig?.rejectedQty || "{}"); // styleColor -> size -> qty

      sizeKeyList.forEach(sizeKey => {
        const reCutGarmentQty =
          reCutGarmentQtyMap[styleColor] && reCutGarmentQtyMap[styleColor][sizeKey] ? reCutGarmentQtyMap[styleColor][sizeKey] : 0;
        const rejectedQty = rejectedQtyMap[styleColor] && rejectedQtyMap[styleColor][sizeKey] ? rejectedQtyMap[styleColor][sizeKey] : 0;
        cutQty[sizeKey] = cutQty[sizeKey] + reCutGarmentQty - rejectedQty;
      });

      // cumulative cut qty
      sizeKeyList.forEach(sizeKey => {
        commCutQty[sizeKey] = (commCutQty[sizeKey] || 0) + cutQty[sizeKey];
      });

      let total = Object.keys(cutQty).reduce((acc, key) => acc + cutQty[key], 0);
      let totalPercent = `${toFixed((total * 100) / totalOrderQty, 1)}%`;
      rows.push({
        ...cutQty,
        label: `Cut Qty${orderFlow.lot > 1 ? `, Lot${lot}` : ""}`,
        styleColor,
        total,
        totalPercent
      });
    }

    if (orderFlow.lot > 1) {
      let totalQty = sizeKeyList.reduce((acc, sizeKey) => ({ ...acc, [sizeKey]: commCutQty[sizeKey] }), {});
      let total = Object.keys(totalQty).reduce((acc, key) => acc + totalQty[key], 0);
      const totalPercent = `${toFixed((total * 100) / totalOrderQty, 1)}%`;
      rows.push({ ...totalQty, styleColor, total, totalPercent, label: "Total" });
    }

    // Process Balance Qty
    let totalBalance = 0;
    let balanceQty = sizeKeyList.reduce((acc, sizeKey) => {
      const balance = commCutQty[sizeKey] - orderQty[sizeKey];
      totalBalance += balance;
      return { ...acc, [sizeKey]: `${signedValue(balance)}(${toFixed((balance * 100) / orderQty[sizeKey], 1)}%)` };
    }, {});
    const totalPercent = `${toFixed((totalBalance * 100) / totalOrderQty, 1)}%`;
    rows.push({ ...balanceQty, styleColor, total: totalBalance, totalPercent, label: "Extra/Shortage" });
  } else if (dataType === DataType.REMARKS_SUMMARY && orderFlow && orderFlow.orderConfig) {
    const alerts = orderFlow.orderConfig.alerts ? JSON.parse(orderFlow.orderConfig.alerts) : {};
    const merchantName = orderFlow.orderConfig.cutPlanApprovedBy;
    const cadPersonName = orderFlow.orderConfig.cadRemarksBy;
    const alertParamMap = Object.values(AlertParemeter).reduce((acc, e) => ({...acc, [e.id]: e.name}), {});
    let remarksData = [
      ...alerts.map(alert => [alertParamMap[alert?.parameter], alert?.reasons?.join(", "), alert?.parameter === AlertParemeter.CUT_PLAN_QTY.id ? merchantName : cadPersonName]),
      ["CAD Remarks", orderFlow.orderConfig.cadRemarks || "-", cadPersonName],
      [
        "Merchant Remarks",
        orderFlow.orderConfig.merchantRemarks || "-",
        `${orderFlow.orderConfig.cutPlanApproval === "Approved" ? merchantName : "Not Approved"}`
      ],
      ["Cutting Remarks", orderFlow.orderConfig.cuttingRemarks || "-", orderFlow.orderConfig.cuttingRemarksBy],
      ["Warehouse Remarks", orderFlow.orderConfig.warehouseRemarks || "-", orderFlow.orderConfig.warehouseRemarksBy]
    ];
    rows = remarksData.map(data => ({
      params: data[0],
      remarks: data[1],
      user: data[2]
    }));
  }
  return rows;
};

export const getEchartOptions = ({ cutOrder, report }) => {
  let options = {};

  if (isEmpty(cutOrder) || isEmpty(report)) return {};

  const {
    fabricUom,
    allocated,
    blanketQty,
    issued,
    missing,
    unused,
    excess,
    shortage,
    usedInLay,
    endLoss,
    netEndBits,
    damaged,
    usedInPanel,
    returned,
    blanketWeight,
    issuedWeight,
    missingWeight,
    unusedWeight,
    usedInLayWeight,
    endLossWeight,
    netEndBitsWeight,
    damagedWeight,
    usedInPanelWeight,
    returnedWeight
  } = report;

  let utilized = 0;
  let wastage = 0;
  if (fabricUom === UOM.METER.value) {
    const totalConsumed = issued + blanketQty - returned;
    const shortageExcess = shortage + excess;
    const netConsumed = totalConsumed + shortageExcess;
    wastage = blanketQty + netEndBits + endLoss + usedInPanel + damaged + missing + unused;
    utilized = Math.max(0, netConsumed - wastage);
  } else {
    const totalConsumed = issuedWeight + blanketWeight - returnedWeight;
    const shortageExcess = 0;
    const netConsumed = totalConsumed + shortageExcess;
    wastage = blanketWeight + netEndBitsWeight + endLossWeight + usedInPanelWeight + damagedWeight + missingWeight + unusedWeight;
    utilized = Math.max(0, netConsumed - wastage);
  }

  // const returnedDetails = JSON.parse(report.returnedDetails || "{}");
  // const returnedBreakup = Object.keys(returnedDetails)
  //   .filter(key => returnedDetails[key] > 0.0)
  //   .map(key => `${key} = ${toFixed(returnedDetails[key], 1)}`)
  //   .join(", ");

  // const missingDetails = JSON.parse(report.missingDetails || "{}");
  // const missingBreakup = Object.keys(missingDetails)
  //   .filter(key => missingDetails[key] > 0.0)
  //   .map(key => `${key} = ${toFixed(missingDetails[key], 1)}`)
  //   .join(", ");

  const shortageExcess = shortage + excess;
  const available = issued + shortageExcess - returned - unused - missing;
  const availableWeight = issuedWeight - returnedWeight - unusedWeight - missingWeight;
  const usedInMarker = usedInLay - endLoss;
  const usedInMarkerWeight = usedInLayWeight - endLossWeight;

  options[DataType.FABRIC_UTILIZATION_SUMMARY] = {
    color: [colorSecondary.main, colorTomatoRed.main],
    tooltip: {
      trigger: "item",
      formatter: "{a} <br/>{b} : {c} ({d}%)"
    },
    legend: {
      x: "center",
      y: "bottom",
      data: ["Utilized", "Wastage"]
    },
    calculable: true,
    series: [
      {
        name: "Fabric Utilization",
        type: "pie",
        radius: "50%",
        center: ["50%", "40%"],
        data: [
          { value: toFixed(utilized), name: "Utilized" },
          { value: toFixed(wastage), name: "Wastage" }
        ],
        itemStyle: {
          normal: {
            label: {
              position: "outer",
              formatter: function (params) {
                return params.percent + "%";
              }
            },
            labelLine: {
              show: false
            }
          },
          emphasis: {
            label: {
              show: true,
              formatter: "{b}\n{d}%"
            }
          }
        }
      }
    ]
  };

  options[DataType.WASTAGE_SUMMMARY] = {
    color: [colorLightGreen.main, colorTomatoRed.main, colorLemom.main, colorSecondary.main, colorOrange.main, colorPrimary.main, colorPurple.main],
    tooltip: {
      trigger: "item",
      formatter: "{a} <br/>{b} : {c} ({d}%)"
    },
    legend: {
      x: "center",
      y: "bottom",
      data: ["Panel Replacement", "End Bits", "End Loss", "Damaged", "Testing Loss", "Missing", "Unused"]
    },
    calculable: true,
    series: [
      {
        name: "Wastage",
        type: "pie",
        radius: "50%",
        center: ["50%", "40%"],
        data:
          fabricUom === UOM.METER.value
            ? [
                { value: toFixed(usedInPanel), name: "Panel Replacement" },
                { value: toFixed(endLoss), name: "End Loss" },
                { value: toFixed(netEndBits), name: "End Bits" },
                { value: toFixed(damaged), name: "Damaged" },
                { value: toFixed(blanketQty), name: "Testing Loss" },
                { value: toFixed(missing), name: "Missing" },
                { value: toFixed(unused), name: "Unused" }
              ]
            : [
                { value: toFixed(usedInPanelWeight), name: "Panel Replacement" },
                { value: toFixed(endLossWeight), name: "End Loss" },
                { value: toFixed(netEndBitsWeight), name: "End Bits" },
                { value: toFixed(damagedWeight), name: "Damaged" },
                { value: toFixed(blanketWeight), name: "Testing Loss" },
                { value: toFixed(missingWeight), name: "Missing" },
                { value: toFixed(unusedWeight), name: "Unused" }
              ],
        itemStyle: {
          normal: {
            label: {
              position: "outer",
              formatter: function (params) {
                return params.percent + "%";
              }
            },
            labelLine: {
              show: false
            }
          },
          emphasis: {
            label: {
              show: true,
              formatter: "{b}\n{d}%"
            }
          }
        }
      }
    ]
  };

  let cuSummaryList =
    fabricUom === UOM.METER.value
      ? [
          { label: "BOM CU", value: toFixed(report.orderingCu, 3) },
          { label: "Std. CU", value: toFixed(report.stdCu, 3) },
          { label: "Plan CU", value: toFixed(report.planCu, 3) },
          { label: "Actual CU", value: toFixed(report.actualCu, 3) },
          { label: "Gross CU", value: toFixed(report.grossCu, 3) }
        ]
      : [
          { label: "BOM CU", value: toFixed(report.orderingCuInKg, 3) },
          { label: "Std. CU", value: toFixed(report.stdCuInKg, 3) },
          { label: "Plan CU", value: toFixed(report.planCuInKg, 3) },
          { label: "Actual CU", value: toFixed(report.actualCuInKg, 3) },
          { label: "Gross CU", value: toFixed(report.grossCuInKg, 3) }
        ];
  if (isEmpty(report.stdCu) || report.stdCu <= 0.0) {
    cuSummaryList = cuSummaryList.filter(c => c.label !== "Std. CU");
  }
  if (cutOrder?.state !== OState.CLOSED.state) {
    cuSummaryList = cuSummaryList.filter(c => c.label !== "Gross CU");
  }

  options[DataType.CONSUMPTION_SUMMARY] = {
    tooltip: {
      trigger: "axis"
    },
    calculable: true,
    xAxis: [
      {
        type: "value",
        boundaryGap: [0, 0.01],
        show: false,
        scale: false,
        min: 0.9 * cuSummaryList.map(e => e.value).sort((a, b) => a - b)[0]
      }
    ],
    grid: {
      left: "1%",
      right: "10%",
      containLabel: true
    },
    yAxis: [
      {
        type: "category",
        data: cuSummaryList.map(e => e.label)
        // axisLabel: {
        //   margin: 8,
        //   rotate: 25,
        //   formatter: name => `${name.split(" ")[0]}\n ${name.split(" ")[1]}`
        // }
      }
    ],
    series: [
      {
        name: "Consumption",
        type: "bar",
        barWidth: 20,
        data: cuSummaryList.map(e => e.value), // [toFixed(report.orderingCu, 3), toFixed(report.layCu, 3), toFixed(report.actualCu, 3)],
        itemStyle: {
          normal: {
            color: function (params) {
              // build a color map as your need.
              var colorList = [colorLightGreen.main, colorSecondary.main, colorTomatoRed.main, colorLemom.main];
              return colorList[params.dataIndex];
            },
            label: {
              show: true,
              position: "right",
              formatter: "{c}"
            }
          }
        }
      }
    ]
  };

  const widthDeviation = JSON.parse(report.rollWidthDeviation || "{}");
  let dataWidthDeviation = [
    { deviation: "Below -7", qty: widthDeviation["-8"] },
    { deviation: "-7 to -5", qty: widthDeviation["-6"] },
    { deviation: "-5 to -3", qty: widthDeviation["-4"] },
    { deviation: "-3 to -1", qty: widthDeviation["-2"] },
    { deviation: "-1 to +1", qty: widthDeviation["0"] },
    { deviation: "+1 to +3", qty: widthDeviation["2"] },
    { deviation: "+3 to +5", qty: widthDeviation["4"] },
    { deviation: "+5 to +7", qty: widthDeviation["6"] },
    { deviation: "Above +7", qty: widthDeviation["8"] }
  ];

  let totalQty = dataWidthDeviation.reduce((acc, el) => acc + el.qty, 0);
  dataWidthDeviation = dataWidthDeviation.map(e => ({ ...e, qtyPercent: parseFloat(toFixed((e.qty * 100) / totalQty, 1)) }));

  const rollVariation = JSON.parse(report.rollLengthVariation);
  let dataLengthVariation = [
    { range: "0-40", qty: rollVariation["40"] },
    { range: "40-60", qty: rollVariation["60"] },
    { range: "60-80", qty: rollVariation["80"] },
    { range: "80-100", qty: rollVariation["100"] },
    { range: "100+", qty: rollVariation["120"] }
  ];

  totalQty = dataLengthVariation.reduce((acc, el) => acc + el.qty, 0);
  dataLengthVariation = dataLengthVariation.map(e => ({ ...e, qtyPercent: parseFloat(toFixed((e.qty * 100) / totalQty, 1)) }));

  options[DataType.ROLL_WIDTH_DEVIATION] = {
    tooltip: {
      trigger: "axis"
    },
    calculable: true,
    xAxis: [
      {
        type: "category",
        data: dataWidthDeviation.map(e => e.deviation),
        axisLabel: {
          rotate: 45
        }
      }
    ],
    yAxis: [
      {
        type: "value",
        show: false
      }
    ],
    series: [
      {
        name: "Width Deviation",
        type: "bar",
        barWidth: 25,
        data: dataWidthDeviation.map(e => e.qtyPercent),
        itemStyle: {
          normal: {
            color: colorSecondary.main,
            label: {
              show: true,
              position: "top",
              formatter: "{c}%"
            }
          }
        }
      }
    ]
  };

  options[DataType.ROLL_LENGTH_VARIATION] = {
    tooltip: {
      trigger: "axis"
    },
    calculable: true,
    xAxis: [
      {
        type: "category",
        data: dataLengthVariation.map(e => e.range),
        axisLabel: {
          rotate: 45
        }
      }
    ],
    yAxis: [
      {
        type: "value",
        show: false
      }
    ],
    series: [
      {
        name: "Length Variation",
        type: "bar",
        data: dataLengthVariation.map(e => e.qtyPercent),
        barWidth: 25,
        itemStyle: {
          normal: {
            color: colorSecondary.main,
            label: {
              show: true,
              position: "top",
              formatter: "{c}%"
            }
          }
        }
      }
    ]
  };

  options[DataType.MARKER_EFFICIENCY] = {
    tooltip: {
      formatter: "{a} <br/>{b} : {c}%"
    },
    series: [
      {
        name: "Efficiency",
        type: "gauge",
        min: 50,
        max: 100,
        splitNumber: 5, // Split the number of segments, the default is 5
        axisLine: {
          lineStyle: {
            color: [
              [0.5, colorTomatoRed.main],
              [0.8, colorSecondary.main],
              [1, colorLightGreen.main]
            ],
            width: 5
          }
        },
        axisTick: {
          splitNumber: 10, // How many segments per split
          length: 12, // Attribute length control line length
          lineStyle: {
            color: "auto"
          }
        },
        axisLabel: {
          textStyle: {
            color: "auto"
          }
        },
        splitLine: {
          show: true, // The default display, the property show control is displayed or not
          length: 20, // Attribute length control line length
          lineStyle: {
            color: "auto"
          }
        },
        pointer: {
          width: 5
        },
        detail: {
          formatter: "{value}%",
          textStyle: {
            color: "auto",
            fontWeight: "bold",
            fontSize: 18
          }
        },
        data: [{ value: toFixed(report.markerEfficiency) }]
      }
    ]
  };

  let rows = [
    // {
    //   serialNo: 0,
    //   classification: "Allocated Fabric",
    //   length: toFixed(allocated, 1)
    //   // percentage: "100.0%",
    //   // remarks:
    //   //   report.lot === report.noOfLots && report.cumCutQty < report.orderQty
    //   //     ? `Required Fabric: ${toFixed(report.orderQty * report.actualCu, 1)}m`
    //   //     : ""
    // },
    {
      serialNo: 1,
      classification: "Issued Fabric",
      length: `${toFixed(fabricUom === UOM.METER.value ? issued : issuedWeight, 1)} ${UomCodeFromValue[fabricUom]}`,
      percentage: "100.0%",
      remarks: `Required Fabric: ${
        fabricUom === UOM.METER.value ? toFixed(report.orderQty * report.actualCu, 1) : toFixed(report.orderQty * report.actualCuInKg, 1)
      } ${UomCodeFromValue[fabricUom]}`
    },
    {
      serialNo: 2,
      classification: "Returned",
      length: `${toFixed(fabricUom === UOM.METER.value ? returned : returnedWeight, 1)} ${UomCodeFromValue[fabricUom]}`,
      percentage: toFixed((returned * 100) / allocated, 2) + "%",
      // remarks: returnedBreakup
    },
    {
      serialNo: 3,
      classification: "Missing Fabric",
      fabric: missing,
      length: `${toFixed(fabricUom === UOM.METER.value ? missing : missingWeight, 1)} ${UomCodeFromValue[fabricUom]}`,
      percentage: toFixed((missing * 100) / allocated, 2) + "%",
      // remarks: missingBreakup
    },
    {
      serialNo: 4,
      classification: "Unused Fabric",
      fabric: unused,
      length: `${toFixed(fabricUom === UOM.METER.value ? unused : unusedWeight, 1)} ${UomCodeFromValue[fabricUom]}`,
      percentage: toFixed((unused * 100) / allocated, 2) + "%",
      remarks: ""
    },
    {
      serialNo: 5,
      classification: "Excess(Meterage)",
      length: `${toFixed(fabricUom === UOM.METER.value ? excess : 0, 1)} ${UomCodeFromValue[fabricUom]}`,
      percentage: toFixed((excess * 100) / allocated, 2) + "%",
      remarks: ""
    },
    {
      serialNo: 6,
      classification: "Shortage(Meterage)",
      length: `${toFixed(fabricUom === UOM.METER.value ? -shortage : 0, 1)} ${UomCodeFromValue[fabricUom]}`,
      percentage: toFixed((-shortage * 100) / allocated, 2) + "%",
      remarks: ""
    },
    {
      serialNo: "",
      classification: "Fabric Available For Use",
      length: `${toFixed(fabricUom === UOM.METER.value ? available : availableWeight, 1)} ${UomCodeFromValue[fabricUom]}`,
      percentage: toFixed((available * 100) / allocated, 2) + "%",
      remarks: ""
    },
    { serialNo: "", classification: "", length: "", percentage: "", remarks: "" },
    {
      serialNo: 1,
      classification: "Used in Markers",
      length: `${toFixed(fabricUom === UOM.METER.value ? usedInMarker : usedInMarkerWeight, 1)} ${UomCodeFromValue[fabricUom]}`,
      percentage: toFixed((usedInMarker * 100) / available, 2) + "%",
      remarks: ""
    },
    {
      serialNo: 2,
      classification: "End Loss",
      length: `${toFixed(fabricUom === UOM.METER.value ? endLoss : endLossWeight, 1)} ${UomCodeFromValue[fabricUom]}`,
      percentage: toFixed((endLoss * 100) / available, 2) + "%",
      remarks: ""
    },
    {
      serialNo: 3,
      classification: "Net End Bits",
      length: `${toFixed(fabricUom === UOM.METER.value ? netEndBits : netEndBitsWeight, 1)} ${UomCodeFromValue[fabricUom]}`,
      percentage: toFixed((netEndBits * 100) / available, 2) + "%",
      remarks: ""
    },
    {
      serialNo: 4,
      classification: "Damaged",
      length: `${toFixed(fabricUom === UOM.METER.value ? damaged : damagedWeight, 1)} ${UomCodeFromValue[fabricUom]}`,
      percentage: toFixed((damaged * 100) / available, 2) + "%",
      remarks: ""
    },
    {
      serialNo: 5,
      classification: "Used in Panel Replacement",
      length: `${toFixed(fabricUom === UOM.METER.value ? usedInPanel : usedInPanelWeight, 1)} ${UomCodeFromValue[fabricUom]}`,
      percentage: toFixed((usedInPanel * 100) / available, 2) + "%",
      remarks: ""
    }
  ];

  options[DataType.FABRIC_UTILIZATION_DETAILS] = rows;

  return options;
};
